import { ApiError } from "@models/error";
import type { ApiErrorResponse } from "@models/error";
import { getAccessTokenSync } from "./tokenStore";

// Global token accessor - set by ContextProvider when BeeHive auth is ready
let globalGetAccessToken: (() => Promise<string | null>) | null = null;

export const setGlobalAccessTokenFn = (
  fn: () => Promise<string | null>,
): void => {
  globalGetAccessToken = fn;
};

export class HttpClient {
  private baseUrl: string;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  private async getAccessToken(): Promise<string | null> {
    try {
      // The global token accessor (set by BeeHive auth)
      if (globalGetAccessToken) {
        const token = await globalGetAccessToken();
        if (token) return token;
      }
      // Fall back to the in-memory store, never to localStorage: a
      // bearer token there is readable by any script on the page.
      return getAccessTokenSync();
    } catch (error) {
      console.error("Error getting access token:", error);
      return getAccessTokenSync();
    }
  }

  public async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const token = await this.getAccessToken();

    if (!token) {
      throw new Error("No access token available. Please log in.");
    }

    const response = await fetch(this.baseUrl + endpoint, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (response.status === 401) {
      throw new Error("Authentication failed. Please log in again.");
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return this.parseResponse<T>(response);
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    if (response.status === 204 || contentLength === "0") {
      return undefined as T;
    }

    if (!contentType?.includes("application/json")) {
      const text = await response.text();
      if (!text) {
        return undefined as T;
      }
      try {
        return JSON.parse(text);
      } catch {
        return undefined as T;
      }
    }

    try {
      return await response.json();
    } catch (error) {
      console.error("Error parsing JSON response:", error);
      return undefined as T;
    }
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    try {
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        const errorData = (await response.json()) as
          | ApiErrorResponse
          | { detail?: string | Record<string, unknown> };

        // Structured app error from the global FatalError handler.
        if (
          "error_code" in errorData &&
          errorData.error_code &&
          "message" in errorData &&
          errorData.message
        ) {
          throw ApiError.fromResponse(
            errorData as ApiErrorResponse,
            response.status,
          );
        }

        // FastAPI's HTTPException returns `{ detail: "..." }`. Surface it as
        // an ApiError too so callers can branch on statusCode (403/404/409).
        if ("detail" in errorData && errorData.detail !== undefined) {
          const detail = errorData.detail;
          const message =
            typeof detail === "string" ? detail : JSON.stringify(detail);
          throw new ApiError(
            message,
            `http_${response.status}`,
            response.status,
          );
        }
      }

      const errorText = await response.text();
      throw new Error(
        `API request failed: ${response.status} ${
          errorText || response.statusText
        }`,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      );
    }
  }
}
